import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { ApiBackend } from "../../providers/apibackend";
import { POSSetting } from "../../model/POSSetting";
import { Product } from "../../model/Product";
import { StripeTerminal } from "../../model/StripeTerminal";
import { getStationSettings, saveStationSettings, isAutoCheckinEnabled, MobilePosStationSettings } from "./mobilePosSettings";
import { isPrinterSupported, connectPrinter, disconnectPrinter, getConnectedPrinterName, printReceipt, ReceiptData } from "./mobilePosPrinter";

/**
 * Fristående, mobiloptimerad incheckningssida (/mobilepos).
 * Skannar QR (order-publicId) med telefonens kamera och checkar in (levererar)
 * produkter enligt vald POS-stations produktlista. Återanvänder Kontrollens
 * inloggning och samma deliver-endpoint som handscanner-appen.
 */

type OrderItem = {
    productId: string;
    productVariantId: string;
    productVariantPriceGroupId: string;
    productVariantOccationId?: string;
    numberOfProducts: number;
    productName?: string;
    variantName?: string;
    occationName?: string;
};

type Delivery = {
    productId: string;
    variantId?: string;
    productVariantId?: string;
    priceGroupId?: string;
    productVariantPriceGroupId?: string;
    occationId?: string;
    productVariantOccationId?: string;
    numDelivered?: number;
};

type Feedback = { type: "success" | "warning" | "error"; title: string; detail?: string } | null;

const READER_ID = "mobilepos-reader";

const numDeliveredFor = (item: OrderItem, deliveries: Delivery[]): number => {
    return deliveries
        .filter(d =>
            d.productId === item.productId &&
            (d.variantId ?? d.productVariantId) === item.productVariantId &&
            (d.priceGroupId ?? d.productVariantPriceGroupId) === item.productVariantPriceGroupId &&
            ((d.occationId ?? d.productVariantOccationId) || "") === (item.productVariantOccationId || ""))
        .reduce((sum, d) => sum + (d.numDelivered ?? 0), 0);
};

const isCheckinTarget = (item: OrderItem, pos: POSSetting): boolean => {
    const list = pos?.ProductList ?? [];
    return list.indexOf(item.productVariantOccationId || "") >= 0
        || list.indexOf(item.productVariantId) >= 0
        || list.indexOf(item.productId) >= 0;
};

type CatalogEntry = { id: string; label: string };

/** Resolvar stationens ProductList (ids) till läsbara etiketter via produktkatalogen. */
const resolveStationProducts = (catalog: Product[], pos: POSSetting): CatalogEntry[] => {
    const out: CatalogEntry[] = [];
    const seen = new Set<string>();
    const add = (id: string, label: string) => { if (id && !seen.has(id)) { seen.add(id); out.push({ id, label }); } };
    for (const pid of (pos?.ProductList ?? [])) {
        for (const p of (catalog || [])) {
            if (p.id === pid) { add(pid, p.name); }
            for (const v of (p.variants || [])) {
                if (v.Id === pid) { add(pid, `${p.name} · ${v.Name}`); }
                for (const o of (v.occations || [])) {
                    if (o.id === pid) { add(pid, `${p.name} · ${v.Name} · ${o.name}`); }
                }
            }
        }
    }
    return out;
};

// ---------- Snabbkassa (försäljning) ----------
type SellUnit = {
    key: string;
    productId: string;
    productName: string;
    productVariantId: string;
    variantName: string;
    productVariantPriceGroupId: string;
    priceGroupName: string;
    price: number;
    productVariantOccationId?: string;
    occationName?: string;
};

type CartLine = { unit: SellUnit; qty: number };

const dateOnly = (s: string): Date => {
    const d = new Date(s);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/** Första giltiga prisgrupp (giltig datum, ej endast-online), likt gamla appen. */
const firstValidPriceGroup = (v: any): any => {
    const today = new Date();
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return (v.priceGroups || []).find((pg: any) => {
        if (pg.validOnlineOnly) { return false; }
        if (pg.validFrom && dateOnly(pg.validFrom) > t) { return false; }
        if (pg.validTo && dateOnly(pg.validTo) < t) { return false; }
        return true;
    });
};

/** Bygger säljbara enheter (variant/occasion) från stationens ProductList. */
const resolveSellUnits = (catalog: Product[], pos: POSSetting): SellUnit[] => {
    const list = pos?.ProductList ?? [];
    const units: SellUnit[] = [];
    for (const p of (catalog || [])) {
        const productMatches = list.indexOf(p.id) >= 0;
        for (const v of (p.variants || [])) {
            const variantMatches = list.indexOf(v.Id) >= 0;
            const matchedOccations = (v.occations || []).filter(o => list.indexOf(o.id) >= 0);
            if (!productMatches && !variantMatches && matchedOccations.length === 0) { continue; }
            const pg = firstValidPriceGroup(v);
            if (!pg) { continue; }
            const base = {
                productId: p.id, productName: p.name,
                productVariantId: v.Id, variantName: v.Name,
                productVariantPriceGroupId: pg.id, priceGroupName: pg.Name, price: pg.price || 0,
            };
            if ((v.occations || []).length > 0) {
                const occs = (productMatches || variantMatches) ? v.occations : matchedOccations;
                for (const o of occs) {
                    units.push({ ...base, key: `${v.Id}:${o.id}`, productVariantOccationId: o.id, occationName: o.name });
                }
            } else {
                units.push({ ...base, key: v.Id });
            }
        }
    }
    return units;
};

const MobilePos = () => {
    const backend = new ApiBackend();
    const [posSettings, setPosSettings] = useState<POSSetting[]>(null);
    const [selectedPos, setSelectedPos] = useState<POSSetting>(null);
    const [scanning, setScanning] = useState(false);
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState<Feedback>(null);
    const [order, setOrder] = useState<any>(null);
    const [deliveries, setDeliveries] = useState<Delivery[]>([]);

    const [catalog, setCatalog] = useState<Product[]>([]);
    const [terminals, setTerminals] = useState<StripeTerminal[]>([]);
    const [settings, setSettings] = useState<MobilePosStationSettings>({});
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Snabbkassa
    const [saleOpen, setSaleOpen] = useState(false);
    const [cart, setCart] = useState<CartLine[]>([]);
    // Betalning: null = ingen pågående, annars aktivt betalläge ("free" = nollsummeorder utan betalning)
    const [pay, setPay] = useState<{ method: "swish" | "stripe" | "free"; status: string; error?: string; done?: boolean } | null>(null);
    const [swishPhone, setSwishPhone] = useState("");
    // Checka in köpta biljetter direkt efter betalning
    const [checkinAfterSale, setCheckinAfterSale] = useState(false);
    // Kvittoutskrift (BLE-skrivare)
    const [printerName, setPrinterName] = useState<string | null>(getConnectedPrinterName());
    const [lastReceipt, setLastReceipt] = useState<ReceiptData | null>(null);
    const [printMsg, setPrintMsg] = useState<string>("");

    const scannerRef = useRef<Html5Qrcode>(null);
    const lastScanRef = useRef<{ code: string; at: number }>({ code: "", at: 0 });
    const handlingRef = useRef(false);
    const payRef = useRef<{ cancelled: boolean; refId?: string }>({ cancelled: false });

    useEffect(() => {
        backend.listPOSSettings().then(list => {
            setPosSettings((list || []).filter(p => p.showCheckin || p.showCheckout));
        });
        return () => { stopScanner(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectStation = async (p: POSSetting) => {
        setSelectedPos(p);
        setSettings(getStationSettings(p.Id));
        const cat = await backend.getGeneral<{ products: Product[] }>("checkout/products?pos=true");
        setCatalog(cat && Array.isArray(cat.products) ? cat.products : []);
        const term = await backend.getGeneral<StripeTerminal[]>("payment/stripe/terminals");
        setTerminals(Array.isArray(term) ? term : []);
    };

    const updateSettings = (next: MobilePosStationSettings) => {
        setSettings(next);
        if (selectedPos) {
            saveStationSettings(selectedPos.Id, next);
        }
    };

    const handleConnectPrinter = async () => {
        try {
            const name = await connectPrinter();
            setPrinterName(name);
            setPrintMsg("Skrivare ansluten: " + name);
        } catch (e: any) {
            setPrintMsg("Kunde inte ansluta skrivare: " + (e?.message || "okänt fel"));
        }
    };
    const handleDisconnectPrinter = () => { disconnectPrinter(); setPrinterName(null); setPrintMsg(""); };

    // ---------- Snabbkassa ----------
    const addToCart = (u: SellUnit) => {
        setCart(prev => {
            const i = prev.findIndex(l => l.unit.key === u.key);
            if (i >= 0) {
                const next = prev.slice();
                next[i] = { ...next[i], qty: next[i].qty + 1 };
                return next;
            }
            return [...prev, { unit: u, qty: 1 }];
        });
    };

    const changeQty = (key: string, delta: number) => {
        setCart(prev => prev
            .map(l => l.unit.key === key ? { ...l, qty: l.qty + delta } : l)
            .filter(l => l.qty > 0));
    };

    const cartTotal = cart.reduce((sum, l) => sum + l.unit.price * l.qty, 0);

    const buildSaleOrder = (phone?: string): any => {
        const vatPercent = selectedPos && selectedPos.VAT ? parseFloat(selectedPos.VAT) || 0 : 0;
        const totalSum = cartTotal;
        const totalVat = vatPercent > 0 ? Math.round((totalSum - totalSum / ((100 + vatPercent) / 100)) * 100) / 100 : 0;
        return {
            createdOnPosId: selectedPos?.Id,
            vatPercentage: vatPercent,
            totalSum,
            totalVat,
            contact: { phone: phone || "" },
            items: cart.map(l => ({
                numberOfProducts: l.qty,
                productId: l.unit.productId,
                productName: l.unit.productName,
                productVariantId: l.unit.productVariantId,
                variantName: l.unit.variantName,
                productVariantPriceGroupId: l.unit.productVariantPriceGroupId,
                priceGroupName: l.unit.priceGroupName,
                itemPrice: l.unit.price,
                productVariantOccationId: l.unit.productVariantOccationId,
                occationName: l.unit.occationName,
            })),
        };
    };

    // Checkar in (levererar) de köpta raderna på stationen. Returnerar antal incheckade produkter.
    const checkinSoldItems = async (orderId: string, lines: CartLine[]): Promise<number> => {
        let ok = 0;
        for (const l of lines) {
            const done = await backend.deliverCheckinProduct(
                orderId, l.unit.productId, l.unit.productVariantId, l.unit.productVariantPriceGroupId,
                l.unit.productVariantOccationId || "", l.qty, selectedPos?.Id
            );
            if (done) { ok += l.qty; }
        }
        return ok;
    };

    const buildReceipt = (order: any, lines: CartLine[]): ReceiptData => {
        const vatPercent = selectedPos && selectedPos.VAT ? parseFloat(selectedPos.VAT) || 0 : 0;
        const totalSum = lines.reduce((sum, l) => sum + l.unit.price * l.qty, 0);
        const totalVat = vatPercent > 0 ? Math.round((totalSum - totalSum / ((100 + vatPercent) / 100)) * 100) / 100 : 0;
        return {
            publicId: order.publicId || order.id,
            orderId: order.id,
            date: new Date(),
            lines: lines.map(l => ({
                qty: l.qty, productName: l.unit.productName, variantName: l.unit.variantName,
                priceGroupName: l.unit.priceGroupName, occationName: l.unit.occationName, itemPrice: l.unit.price,
            })),
            totalSum, vatPercentage: vatPercent, totalVat,
        };
    };

    // Sparar kvittodata och skriver ut automatiskt om påslaget + skrivare ansluten
    const completeReceipt = async (order: any, lines: CartLine[]) => {
        const rec = buildReceipt(order, lines);
        setLastReceipt(rec);
        if (settings.autoPrintReceipt && getConnectedPrinterName()) {
            try {
                setPrintMsg("Skriver ut kvitto…");
                await printReceipt(rec);
                setPrintMsg("Kvitto utskrivet");
            } catch (e: any) {
                setPrintMsg("Utskrift misslyckades: " + (e?.message || "okänt fel"));
            }
        }
    };

    const printLastReceipt = async () => {
        if (!lastReceipt) { return; }
        try {
            setPrintMsg("Skriver ut kvitto…");
            await printReceipt(lastReceipt);
            setPrintMsg("Kvitto utskrivet");
        } catch (e: any) {
            setPrintMsg("Utskrift misslyckades: " + (e?.message || "okänt fel"));
        }
    };

    // Pollar betalstatus tills PAID/DECLINED/ERROR eller avbrutet
    const pollPayment = async (method: "swish" | "stripe", refId: string, order: any, lines: CartLine[]) => {
        while (!payRef.current.cancelled) {
            await new Promise(r => setTimeout(r, 1500));
            if (payRef.current.cancelled) { return; }
            const status = method === "swish"
                ? await backend.checkSwishPaymentStatus(refId)
                : await backend.checkStripePaymentStatus(refId);
            if (status === "PAID") {
                setCart([]);
                beep(true);
                if (checkinAfterSale && order.id && lines.length > 0) {
                    setPay({ method, status: "Betald – checkar in…", done: true });
                    const okItems = await checkinSoldItems(order.id, lines);
                    const totalItems = lines.reduce((sum, l) => sum + l.qty, 0);
                    setPay({
                        method, done: true,
                        status: okItems >= totalItems
                            ? `Betald · ${okItems} incheckade`
                            : `Betald · ${okItems}/${totalItems} incheckade – kontrollera manuellt`,
                    });
                } else {
                    setPay({ method, status: "Betald", done: true });
                }
                await completeReceipt(order, lines);
                return;
            }
            if (status === "DECLINED") {
                setPay({ method, status: "Avbruten", error: "Betalningen avbröts." });
                return;
            }
            if (status === "ERROR") {
                setPay({ method, status: "Fel", error: "Ett fel uppstod vid betalning." });
                return;
            }
        }
    };

    const startStripePayment = async () => {
        if (!settings.stripeTerminalId) {
            setPay({ method: "stripe", status: "Fel", error: "Ingen Stripe-terminal vald i inställningarna." });
            return;
        }
        payRef.current = { cancelled: false };
        const lines = cart.slice();
        setPay({ method: "stripe", status: "Skapar order…" });
        const order = await backend.createOrder(buildSaleOrder() as any);
        if (!order || !order.id) {
            setPay({ method: "stripe", status: "Fel", error: "Kunde inte skapa order." });
            return;
        }
        setPay({ method: "stripe", status: "Slutför på terminalen…" });
        const refId = await backend.initializeStripePayment(order.id, settings.stripeTerminalId, selectedPos?.pointOfSale);
        if (!refId) {
            setPay({ method: "stripe", status: "Fel", error: "Kunde inte initiera kortbetalning." });
            return;
        }
        payRef.current.refId = refId;
        await pollPayment("stripe", refId, order, lines);
    };

    const startSwishPayment = async () => {
        if (!swishPhone.trim()) {
            setPay({ method: "swish", status: "Fel", error: "Ange kundens mobilnummer." });
            return;
        }
        payRef.current = { cancelled: false };
        const lines = cart.slice();
        setPay({ method: "swish", status: "Skapar order…" });
        const order = await backend.createOrder(buildSaleOrder(swishPhone.trim()) as any);
        if (!order || !order.id) {
            setPay({ method: "swish", status: "Fel", error: "Kunde inte skapa order." });
            return;
        }
        setPay({ method: "swish", status: "Initierar Swish…" });
        const refId = await backend.initializeSwishPayment(order.id);
        if (!refId) {
            setPay({ method: "swish", status: "Fel", error: "Kunde inte initiera Swish." });
            return;
        }
        payRef.current.refId = refId;
        setPay({ method: "swish", status: "Be kunden godkänna i Swish-appen…" });
        await pollPayment("swish", refId, order, lines);
    };

    // Nollsummeorder (t.ex. förbokning) – skapar bara ordern, ingen betalning
    const orderWithoutPayment = async () => {
        payRef.current = { cancelled: false };
        const lines = cart.slice();
        setPay({ method: "free", status: "Skapar order…" });
        const order = await backend.createOrder(buildSaleOrder() as any);
        if (!order || !order.id) {
            setPay({ method: "free", status: "Fel", error: "Kunde inte skapa order." });
            return;
        }
        setCart([]);
        beep(true);
        if (checkinAfterSale && lines.length > 0) {
            setPay({ method: "free", status: "Beställd – checkar in…", done: true });
            const okItems = await checkinSoldItems(order.id, lines);
            const totalItems = lines.reduce((sum, l) => sum + l.qty, 0);
            setPay({
                method: "free", done: true,
                status: okItems >= totalItems
                    ? `Beställd · ${okItems} incheckade`
                    : `Beställd · ${okItems}/${totalItems} incheckade – kontrollera manuellt`,
            });
        } else {
            setPay({ method: "free", status: "Beställd", done: true });
        }
        await completeReceipt(order, lines);
    };

    const cancelPayment = () => {
        payRef.current.cancelled = true;
        if (pay?.method === "stripe" && payRef.current.refId) {
            backend.cancelStripePayment(payRef.current.refId);
        }
        setPay(null);
    };

    const closePayment = () => {
        payRef.current.cancelled = true;
        const wasDone = pay?.done;
        setPay(null);
        setPrintMsg("");
        if (wasDone) {
            setSwishPhone("");
            setSaleOpen(false);
        }
    };

    const stopScanner = () => {
        const s = scannerRef.current;
        if (s) {
            s.stop().then(() => s.clear()).catch(() => { });
            scannerRef.current = null;
        }
    };

    const startScanner = async () => {
        setFeedback(null);
        setScanning(true);
        // Vänta in att reader-diven renderats
        setTimeout(async () => {
            try {
                const scanner = new Html5Qrcode(READER_ID, false);
                scannerRef.current = scanner;
                await scanner.start(
                    { facingMode: "environment" },
                    { fps: 10, qrbox: { width: 240, height: 240 } },
                    (decoded) => onScan(decoded),
                    () => { }
                );
            } catch (e) {
                setScanning(false);
                setFeedback({ type: "error", title: "Kameran kunde inte startas", detail: "Ge sidan kameråtkomst och försök igen." });
            }
        }, 50);
    };

    const onScan = async (decodedText: string) => {
        const now = Date.now();
        // Debounce: ignorera samma kod inom 2,5s och medan ett anrop pågår
        if (handlingRef.current) { return; }
        if (decodedText === lastScanRef.current.code && now - lastScanRef.current.at < 2500) { return; }
        lastScanRef.current = { code: decodedText, at: now };
        handlingRef.current = true;
        try {
            await handleCode(decodedText);
        } finally {
            handlingRef.current = false;
        }
    };

    const loadDeliveries = async (orderId: string): Promise<Delivery[]> => {
        const d = await backend.getOrderDeliveries(orderId);
        setDeliveries(d || []);
        return d || [];
    };

    const handleCode = async (rawCode: string) => {
        setBusy(true);
        setFeedback(null);
        const code = (rawCode || "").trim().replace(/\+/g, "-");
        const fetched = await backend.retrievePublicOrder(code);

        if (!fetched) {
            setOrder(null); setDeliveries([]);
            setFeedback({ type: "error", title: "Okänd order", detail: code });
            beep(false);
            setBusy(false);
            return;
        }
        if (fetched.Status !== "Completed") {
            setOrder(fetched); setDeliveries([]);
            setFeedback({ type: "error", title: "Ogiltig order", detail: "Status: " + fetched.Status });
            beep(false);
            setBusy(false);
            return;
        }

        setOrder(fetched);
        const dels = await loadDeliveries(fetched.id);

        const items: OrderItem[] = (fetched.items || []).filter((it: OrderItem) => isCheckinTarget(it, selectedPos));
        if (items.length === 0) {
            setFeedback({ type: "warning", title: "Ingen produkt för denna station", detail: contactName(fetched) });
            beep(false);
            setBusy(false);
            return;
        }

        // Auto-incheckning respekterar inställningen (vilka produkter som auto-incheckas)
        const autoItems = items.filter(it => isAutoCheckinEnabled(settings, [it.productVariantOccationId, it.productVariantId, it.productId]));
        const toDeliver = autoItems.find(it => numDeliveredFor(it, dels) < it.numberOfProducts);
        if (!toDeliver) {
            const allDone = items.every(it => numDeliveredFor(it, dels) >= it.numberOfProducts);
            setFeedback({
                type: "warning",
                title: allDone ? "Redan incheckad" : "Manuell incheckning krävs",
                detail: contactName(fetched)
            });
            beep(false);
            setBusy(false);
            return;
        }

        const ok = await deliverItem(fetched, toDeliver, dels);
        setBusy(false);
        if (!ok) {
            setFeedback({ type: "error", title: "Incheckning misslyckades", detail: labelForItem(toDeliver) });
            beep(false);
        }
    };

    const deliverItem = async (ord: any, item: OrderItem, currentDeliveries: Delivery[]): Promise<boolean> => {
        const ok = await backend.deliverCheckinProduct(
            ord.id, item.productId, item.productVariantId, item.productVariantPriceGroupId,
            item.productVariantOccationId || "", 1, selectedPos?.Id
        );
        if (ok) {
            const dels = await loadDeliveries(ord.id);
            const remaining = (ord.items || [])
                .filter((it: OrderItem) => isCheckinTarget(it, selectedPos))
                .reduce((sum: number, it: OrderItem) => sum + Math.max(0, it.numberOfProducts - numDeliveredFor(it, dels)), 0);
            setFeedback({
                type: "success",
                title: "Incheckad — " + contactName(ord),
                detail: labelForItem(item) + (remaining > 0 ? ` · ${remaining} kvar i ordern` : "")
            });
            beep(true);
        }
        return ok;
    };

    const manualDeliver = async (item: OrderItem) => {
        if (!order || busy) { return; }
        setBusy(true);
        const ok = await deliverItem(order, item, deliveries);
        setBusy(false);
        if (!ok) {
            setFeedback({ type: "error", title: "Incheckning misslyckades", detail: labelForItem(item) });
        }
    };

    const contactName = (ord: any): string => {
        const c = ord && ord.contact;
        return c ? `${c.firstName || ""} ${c.lastName || ""}`.trim() : "";
    };

    const labelForItem = (item: OrderItem): string => {
        return [item.productName, item.variantName, item.occationName].filter(Boolean).join(" · ");
    };

    const beep = (success: boolean) => {
        try {
            const ctx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain); gain.connect(ctx.destination);
            osc.frequency.value = success ? 880 : 220;
            gain.gain.value = 0.15;
            osc.start();
            setTimeout(() => { osc.stop(); ctx.close(); }, success ? 120 : 320);
        } catch { /* ignore */ }
    };

    // ---------- Render ----------
    const bannerColor = (t: string) => t === "success" ? "#1f7a3d" : t === "warning" ? "#b07d1e" : "#b3382a";

    if (posSettings === null) {
        return <div style={styles.center}>Laddar stationer…</div>;
    }

    if (!selectedPos) {
        return (
            <div style={styles.page}>
                <div style={styles.header}>
                    <span>Incheckning</span>
                    <button style={styles.linkBtn} onClick={() => ApiBackend.firebase.auth().signOut()}>Logga ut</button>
                </div>
                <div style={styles.body}>
                    <h2 style={{ marginTop: 0 }}>Välj station</h2>
                    {posSettings.length === 0 &&
                        <p style={{ color: "#888" }}>Inga stationer med incheckning aktiverad hittades.</p>}
                    <div style={styles.stationList}>
                        {posSettings.map(p =>
                            <button key={p.Id} style={styles.stationBtn} onClick={() => selectStation(p)}>
                                {p.Name}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ---------- Snabbkassa ----------
    if (saleOpen) {
        const sellUnits = resolveSellUnits(catalog, selectedPos);
        return (
            <div style={styles.page}>
                <div style={styles.header}>
                    <button style={styles.linkBtn} onClick={() => { if (!pay) { setSaleOpen(false); } }}>‹ Tillbaka</button>
                    <span style={{ fontWeight: 600 }}>Snabbkassa</span>
                    <span style={{ width: 40 }} />
                </div>
                <div style={{ ...styles.body, paddingBottom: cart.length > 0 ? 220 : 14 }}>
                    {sellUnits.length === 0 &&
                        <div style={{ color: "#888" }}>Inga säljbara produkter konfigurerade för denna station.</div>}
                    <div style={styles.productGrid}>
                        {sellUnits.map(u =>
                            <button key={u.key} style={styles.productBtn} onClick={() => addToCart(u)}>
                                <div style={{ fontWeight: 600 }}>{u.productName}</div>
                                <div style={{ fontSize: 13, color: "#555" }}>
                                    {[u.variantName, u.occationName].filter(Boolean).join(" · ")}
                                </div>
                                <div style={{ marginTop: 6, fontWeight: 700, color: "#2772b6" }}>{u.price} kr</div>
                            </button>
                        )}
                    </div>
                </div>

                {cart.length > 0 &&
                    <div style={styles.cartBar}>
                        <div style={{ maxHeight: 150, overflowY: "auto", marginBottom: 8 }}>
                            {cart.map(l =>
                                <div key={l.unit.key} style={styles.cartRow}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 14 }}>{[l.unit.productName, l.unit.variantName, l.unit.occationName].filter(Boolean).join(" · ")}</div>
                                        <div style={{ fontSize: 12, color: "#888" }}>{l.unit.price} kr/st</div>
                                    </div>
                                    <button style={styles.qtyBtn} onClick={() => changeQty(l.unit.key, -1)}>−</button>
                                    <span style={{ minWidth: 24, textAlign: "center" }}>{l.qty}</span>
                                    <button style={styles.qtyBtn} onClick={() => changeQty(l.unit.key, 1)}>+</button>
                                </div>
                            )}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
                            <span>Totalt</span><span>{cartTotal} kr</span>
                        </div>
                        <label style={styles.checkinRow}>
                            <input type="checkbox" checked={checkinAfterSale} onChange={e => setCheckinAfterSale(e.target.checked)} />
                            <span>Checka in köpta biljetter direkt</span>
                        </label>
                        {cartTotal === 0
                            ? <button style={{ ...styles.payBtn, background: "#2772b6" }} onClick={orderWithoutPayment}>Beställ utan betalning</button>
                            : <div style={{ display: "flex", gap: 10 }}>
                                <button style={{ ...styles.payBtn, background: "#1f9d55" }} onClick={() => { setSwishPhone(""); setPay({ method: "swish", status: "phone" }); }}>Swish</button>
                                <button style={{ ...styles.payBtn, background: "#635bff" }} onClick={startStripePayment}>Kort</button>
                            </div>
                        }
                    </div>
                }

                {pay &&
                    <div style={styles.overlay}>
                        <div style={styles.payCard}>
                            {pay.method === "swish" && pay.status === "phone" ?
                                <>
                                    <h3 style={{ marginTop: 0 }}>Swish – {cartTotal} kr</h3>
                                    <div style={{ color: "#888", fontSize: 14, marginBottom: 10 }}>Ange kundens mobilnummer.</div>
                                    <input style={styles.phoneInput} type="tel" inputMode="tel" placeholder="07XXXXXXXX"
                                        value={swishPhone} onChange={e => setSwishPhone(e.target.value)} />
                                    <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                                        <button style={{ ...styles.payBtn, background: "#999" }} onClick={() => setPay(null)}>Avbryt</button>
                                        <button style={{ ...styles.payBtn, background: "#1f9d55" }} onClick={startSwishPayment}>Starta betalning</button>
                                    </div>
                                </>
                                :
                                <>
                                    <h3 style={{ marginTop: 0 }}>
                                        {pay.method === "free" ? "Beställning" : pay.method === "swish" ? "Swish" : "Kortbetalning"}
                                        {cartTotal > 0 ? ` – ${cartTotal} kr` : ""}
                                    </h3>
                                    <div style={{ fontSize: 17, margin: "10px 0", color: pay.error ? "#b3382a" : pay.done ? "#1f7a3d" : "#333" }}>
                                        {pay.error || pay.status}
                                    </div>
                                    {!pay.done && !pay.error &&
                                        <div style={{ color: "#888", fontSize: 14, marginBottom: 12 }}>Väntar…</div>}
                                    {pay.done && printMsg && <div style={{ fontSize: 13, color: "#555", marginBottom: 8 }}>{printMsg}</div>}
                                    <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                                        {pay.done || pay.error ?
                                            <>
                                                {pay.done && lastReceipt && isPrinterSupported() &&
                                                    <button style={{ ...styles.payBtn, background: "#555" }} onClick={printLastReceipt}>Skriv ut kvitto</button>}
                                                <button style={{ ...styles.payBtn, background: "#2772b6" }} onClick={closePayment}>Klar</button>
                                            </>
                                            : pay.method !== "free" &&
                                            <button style={{ ...styles.payBtn, background: "#b3382a" }} onClick={cancelPayment}>Avbryt betalning</button>
                                        }
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                }
            </div>
        );
    }

    // ---------- Inställningar ----------
    if (settingsOpen) {
        const stationProducts = resolveStationProducts(catalog, selectedPos);
        const enabledIds = settings.autoCheckinIds ?? stationProducts.map(x => x.id);
        const toggleAuto = (id: string) => {
            const set = new Set(enabledIds);
            if (set.has(id)) { set.delete(id); } else { set.add(id); }
            updateSettings({ ...settings, autoCheckinIds: Array.from(set) });
        };
        return (
            <div style={styles.page}>
                <div style={styles.header}>
                    <button style={styles.linkBtn} onClick={() => setSettingsOpen(false)}>‹ Klar</button>
                    <span style={{ fontWeight: 600 }}>Inställningar</span>
                    <span style={{ width: 40 }} />
                </div>
                <div style={styles.body}>
                    <div style={styles.orderCard}>
                        <h3 style={{ marginTop: 0 }}>Stripe-terminal</h3>
                        <div style={{ color: "#888", fontSize: 13, marginBottom: 8 }}>Terminal att ta kortbetalning på (denna enhet).</div>
                        {terminals.length === 0 && <div style={{ color: "#888" }}>Inga terminaler hittades.</div>}
                        {terminals.map(t =>
                            <label key={t.id} style={styles.settingRow}>
                                <input type="radio" name="terminal" checked={settings.stripeTerminalId === t.id}
                                    onChange={() => updateSettings({ ...settings, stripeTerminalId: t.id })} />
                                <span>{t.name}</span>
                            </label>
                        )}
                        {settings.stripeTerminalId &&
                            <button style={styles.linkClear} onClick={() => updateSettings({ ...settings, stripeTerminalId: undefined })}>Rensa val</button>}
                    </div>

                    <div style={styles.orderCard}>
                        <h3 style={{ marginTop: 0 }}>Auto-incheckning</h3>
                        <div style={{ color: "#888", fontSize: 13, marginBottom: 8 }}>Produkter som checkas in automatiskt vid skanning. Avmarkerade kräver manuell incheckning.</div>
                        {stationProducts.length === 0 && <div style={{ color: "#888" }}>Inga produkter i stationens lista.</div>}
                        {stationProducts.map(sp =>
                            <label key={sp.id} style={styles.settingRow}>
                                <input type="checkbox" checked={enabledIds.indexOf(sp.id) >= 0} onChange={() => toggleAuto(sp.id)} />
                                <span>{sp.label}</span>
                            </label>
                        )}
                    </div>

                    <div style={styles.orderCard}>
                        <h3 style={{ marginTop: 0 }}>Kvittoskrivare (Bluetooth)</h3>
                        {!isPrinterSupported()
                            ? <div style={{ color: "#b3382a", fontSize: 13 }}>Den här enheten stödjer inte Bluetooth-utskrift (fungerar på Android, inte iPhone).</div>
                            : <>
                                <div style={{ color: "#888", fontSize: 13, marginBottom: 8 }}>
                                    {printerName ? `Ansluten: ${printerName}` : "Ingen skrivare ansluten."}
                                </div>
                                <div style={{ display: "flex", gap: 10 }}>
                                    <button style={styles.secondaryBtn} onClick={handleConnectPrinter}>
                                        {printerName ? "Byt skrivare" : "Anslut skrivare"}
                                    </button>
                                    {printerName &&
                                        <button style={styles.secondaryBtn} onClick={handleDisconnectPrinter}>Koppla från</button>}
                                </div>
                                <label style={{ ...styles.settingRow, marginTop: 4 }}>
                                    <input type="checkbox" checked={!!settings.autoPrintReceipt}
                                        onChange={e => updateSettings({ ...settings, autoPrintReceipt: e.target.checked })} />
                                    <span>Skriv ut kvitto automatiskt efter köp</span>
                                </label>
                                {printMsg && <div style={{ fontSize: 13, color: "#555", marginTop: 6 }}>{printMsg}</div>}
                            </>
                        }
                    </div>
                </div>
            </div>
        );
    }

    const targetItems: OrderItem[] = order ? (order.items || []).filter((it: OrderItem) => isCheckinTarget(it, selectedPos)) : [];

    return (
        <div style={styles.page}>
            <div style={styles.header}>
                <button style={styles.linkBtn} onClick={() => { stopScanner(); setScanning(false); setSelectedPos(null); setOrder(null); setFeedback(null); }}>‹ Station</button>
                <span style={{ fontWeight: 600 }}>{selectedPos.Name}</span>
                <div style={{ display: "flex", gap: 12 }}>
                    <button style={styles.linkBtn} onClick={() => { stopScanner(); setScanning(false); setSettingsOpen(true); }}>⚙</button>
                    <button style={styles.linkBtn} onClick={() => ApiBackend.firebase.auth().signOut()}>Logga ut</button>
                </div>
            </div>

            {feedback &&
                <div style={{ ...styles.banner, background: bannerColor(feedback.type) }}>
                    <div style={styles.bannerTitle}>{feedback.title}</div>
                    {feedback.detail && <div style={styles.bannerDetail}>{feedback.detail}</div>}
                </div>
            }

            <div style={styles.body}>
                {selectedPos.showCheckout &&
                    <button style={{ ...styles.primaryBtn, background: "#1f9d55" }} onClick={() => { stopScanner(); setScanning(false); setSaleOpen(true); }}>Snabbkassa</button>
                }
                {selectedPos.showCheckin && !scanning &&
                    <button style={styles.primaryBtn} onClick={startScanner}>Starta skanning</button>
                }

                <div id={READER_ID} style={{ width: "100%", display: scanning ? "block" : "none", borderRadius: 12, overflow: "hidden" }} />

                {scanning &&
                    <button style={styles.secondaryBtn} onClick={() => { stopScanner(); setScanning(false); }}>Stoppa kameran</button>
                }

                {busy && <div style={{ textAlign: "center", padding: 12, color: "#555" }}>Arbetar…</div>}

                {order &&
                    <div style={styles.orderCard}>
                        <div style={styles.orderHead}>
                            <div style={{ fontWeight: 700 }}>{order.publicId}</div>
                            <div>{contactName(order)}</div>
                        </div>
                        {targetItems.length === 0 &&
                            <div style={{ color: "#888", padding: "6px 0" }}>Inga incheckningsbara produkter för denna station.</div>}
                        {targetItems.map((it, idx) => {
                            const delivered = numDeliveredFor(it, deliveries);
                            const full = delivered >= it.numberOfProducts;
                            return (
                                <div key={idx} style={styles.itemRow}>
                                    <div style={{ flex: 1 }}>
                                        <div>{labelForItem(it)}</div>
                                        <div style={{ fontSize: 13, color: full ? "#1f7a3d" : "#888" }}>
                                            Incheckade: {delivered} / {it.numberOfProducts}
                                        </div>
                                    </div>
                                    <button
                                        style={{ ...styles.deliverBtn, opacity: full || busy ? 0.4 : 1 }}
                                        disabled={full || busy}
                                        onClick={() => manualDeliver(it)}
                                    >
                                        {full ? "Klar" : "Checka in"}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                }
            </div>
        </div>
    );
};

const styles: { [k: string]: React.CSSProperties } = {
    page: { maxWidth: 520, margin: "0 auto", minHeight: "100vh", background: "#f4f6f8", display: "flex", flexDirection: "column" },
    center: { display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", color: "#666" },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 14px", background: "#2772b6", color: "#fff", fontSize: 17, position: "sticky", top: 0, zIndex: 5 },
    linkBtn: { background: "transparent", border: "none", color: "#fff", fontSize: 16, padding: 0, cursor: "pointer" },
    body: { padding: 14, display: "flex", flexDirection: "column", gap: 14 },
    stationList: { display: "flex", flexDirection: "column", gap: 10 },
    stationBtn: { padding: "18px 16px", fontSize: 18, borderRadius: 12, border: "1px solid #d0d7de", background: "#fff", textAlign: "left", cursor: "pointer" },
    primaryBtn: { padding: "18px", fontSize: 19, fontWeight: 600, borderRadius: 12, border: "none", background: "#2772b6", color: "#fff", cursor: "pointer" },
    secondaryBtn: { padding: "14px", fontSize: 16, borderRadius: 12, border: "1px solid #d0d7de", background: "#fff", cursor: "pointer" },
    banner: { color: "#fff", padding: "18px 16px", textAlign: "center" },
    bannerTitle: { fontSize: 22, fontWeight: 700 },
    bannerDetail: { fontSize: 15, marginTop: 4, opacity: 0.95 },
    orderCard: { background: "#fff", borderRadius: 12, border: "1px solid #e0e5ec", padding: 12 },
    orderHead: { display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: 8, marginBottom: 8 },
    itemRow: { display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderTop: "1px solid #f2f2f2" },
    deliverBtn: { padding: "12px 16px", fontSize: 16, fontWeight: 600, borderRadius: 10, border: "none", background: "#1f7a3d", color: "#fff", cursor: "pointer", minWidth: 110 },
    settingRow: { display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: "1px solid #f2f2f2", fontSize: 16, cursor: "pointer" },
    linkClear: { background: "transparent", border: "none", color: "#b3382a", fontSize: 14, padding: "8px 0 0", cursor: "pointer" },
    productGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
    productBtn: { padding: "14px 12px", borderRadius: 12, border: "1px solid #d0d7de", background: "#fff", textAlign: "left", cursor: "pointer", minHeight: 90 },
    cartBar: { position: "sticky", bottom: 0, background: "#fff", borderTop: "1px solid #d0d7de", padding: 14, boxShadow: "0 -4px 12px rgba(0,0,0,0.08)" },
    cartRow: { display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid #f2f2f2" },
    checkinRow: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0", fontSize: 15, cursor: "pointer" },
    qtyBtn: { width: 40, height: 40, fontSize: 22, borderRadius: 8, border: "1px solid #d0d7de", background: "#f4f6f8", cursor: "pointer" },
    payBtn: { flex: 1, padding: "16px", fontSize: 18, fontWeight: 700, borderRadius: 12, border: "none", color: "#fff", cursor: "pointer" },
    overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 20 },
    payCard: { background: "#fff", borderRadius: 16, padding: 22, width: "100%", maxWidth: 380 },
    phoneInput: { width: "100%", padding: "14px", fontSize: 18, borderRadius: 10, border: "1px solid #d0d7de", boxSizing: "border-box" },
};

export default MobilePos;
